import React, { useCallback, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { apiJson } from '../../utils/api';
import { parseMediaBundle } from '../../utils/media';
import { thumbnailUrl, withFileAuth } from '../../utils/secureFiles';
import { plainTextOf } from '../../utils/messageActions';
import { canPreview, openFileViewer } from '../../utils/fileViewer';
import { Icon } from '../Icon';

const PanelShell = ({ title, icon, onClose, children, actions }) => (
    <div className="search-panel side-panel" role="dialog" aria-label={title}>
        <div className="side-panel-head">
            <strong>{icon && <Icon name={icon} size={18} />}{title}</strong>
            <span className="side-panel-actions">
                {actions}
                <button className="nav-icon-btn" onClick={onClose} aria-label="닫기" title="닫기"><Icon name="x" /></button>
            </span>
        </div>
        <div className="search-panel-results">{children}</div>
    </div>
);

const when = (ts) => new Date(ts).toLocaleString([], { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });

// ── 저장한 메시지 ───────────────────────────────────────────
export function SavedPanel({ onSelect, onRemove, onClose }) {
    const [items, setItems] = useState(null);
    const load = useCallback(() => {
        apiJson('/api/saved').then(d => setItems(d.items || [])).catch(() => setItems([]));
    }, []);
    useEffect(load, [load]);

    const remove = async (item) => {
        await onRemove(item.channelId, item.messageId);
        setItems(list => list.filter(x => !(x.channelId === item.channelId && x.messageId === item.messageId)));
    };

    return (
        <PanelShell title="저장한 메시지" icon="bookmark" onClose={onClose}>
            {items === null && <div className="search-panel-empty">불러오는 중...</div>}
            {items?.length === 0 && <div className="search-panel-empty">저장한 메시지가 없습니다.<br /><span className="muted">메시지의 🔖 버튼(모바일은 길게 누르기)으로 저장할 수 있어요.</span></div>}
            {items?.map(item => (
                <div key={`${item.channelId}-${item.messageId}`} className="search-panel-item saved-item">
                    <div className="saved-item-main" onClick={() => item.message && onSelect(item.channelId, item.messageId)}>
                        <div className="search-panel-meta"># {item.channelName}{item.message ? ` · ${item.message.username} · ${when(item.message.timestamp)}` : ''}</div>
                        <div className="search-panel-text">{item.message ? plainTextOf(item.message).slice(0, 200) : <em className="muted">삭제된 메시지입니다.</em>}</div>
                    </div>
                    <button className="saved-remove" title="저장 취소" aria-label="저장 취소" onClick={() => remove(item)}><Icon name="x" size={16} /></button>
                </div>
            ))}
        </PanelShell>
    );
}

// ── 채널 파일 모아보기 ──────────────────────────────────────
const parseFileLinks = (message) => {
    if (!message.includes('file-link')) return [];
    const doc = new DOMParser().parseFromString(DOMPurify.sanitize(message), 'text/html');
    return [...doc.querySelectorAll('a.file-link')].map(a => ({
        href: a.getAttribute('href'),
        name: a.textContent.replace(/^📎\s*/, '').trim(),
    })).filter(f => f.href && /^\/(api\/upload-download|uploads)\//.test(f.href));
};

export function FilesPanel({ channelId, onSelect, onMediaClick, onClose }) {
    const [tab, setTab] = useState('media');
    const [items, setItems] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async (before) => {
        setLoading(true);
        try {
            const data = await apiJson(`/api/channels/${channelId}/files?limit=40${before ? `&before=${before}` : ''}`);
            setItems(prev => (before ? [...prev, ...data.items] : data.items));
            setHasMore(data.hasMore);
        } catch { if (!before) setItems([]); }
        finally { setLoading(false); }
    }, [channelId]);
    useEffect(() => { load(); }, [load]);

    const media = [];
    const files = [];
    for (const item of items) {
        const bundle = parseMediaBundle(item.message);
        if (bundle) bundle.forEach((file, index) => media.push({ item, file, bundle, index }));
        else parseFileLinks(item.message).forEach(file => files.push({ item, file }));
    }

    return (
        <PanelShell title="채널 파일" icon="folder" onClose={onClose}>
            <div className="files-tabs">
                <button className={tab === 'media' ? 'active' : ''} onClick={() => setTab('media')}>사진·영상 {media.length || ''}</button>
                <button className={tab === 'files' ? 'active' : ''} onClick={() => setTab('files')}>파일 {files.length || ''}</button>
            </div>
            {tab === 'media' && (
                <div className="files-grid">
                    {media.map(({ item, file, index }, all) => (
                        // 모아보기에서는 메시지별 묶음이 아니라 화면의 사진·영상 전체를 한 묶음으로 보고 앞뒤로 넘긴다
                        <button key={`${item.id}-${index}`} className="files-thumb" title={`${item.username} · ${when(item.timestamp)}`}
                            onClick={() => onMediaClick(media.map(m => m.file), all)}>
                            {file.mimetype.startsWith('video/')
                                ? <><video src={`${withFileAuth(file.fileUrl)}#t=0.1`} muted playsInline preload="metadata" /><span className="files-video">▶</span></>
                                : <img src={thumbnailUrl(file.fileUrl)} alt={file.originalName} loading="lazy" />}
                        </button>
                    ))}
                </div>
            )}
            {tab === 'files' && files.map(({ item, file }, i) => (
                <div key={`${item.id}-${i}`} className="search-panel-item files-row">
                    <a className="files-name" href={withFileAuth(file.href)} target="_blank" rel="noopener noreferrer"
                        onClick={e => { if (canPreview(file.name)) { e.preventDefault(); openFileViewer({ url: file.href, downloadUrl: file.href, name: file.name }); } }}>
                        {canPreview(file.name) ? '👁' : '📎'} {file.name}
                    </a>
                    <button className="files-jump" onClick={() => onSelect(channelId, item.id)}>{item.username} · {when(item.timestamp)} ›</button>
                </div>
            ))}
            {!loading && ((tab === 'media' && !media.length) || (tab === 'files' && !files.length)) && (
                <div className="search-panel-empty">{tab === 'media' ? '이 채널에 올라온 사진·영상이 없습니다.' : '이 채널에 올라온 파일이 없습니다.'}</div>
            )}
            {loading && <div className="search-panel-empty">불러오는 중...</div>}
            {!loading && hasMore && (
                <button className="files-more" onClick={() => load(items[items.length - 1]?.id)}>더 불러오기</button>
            )}
        </PanelShell>
    );
}
